import { useMemo } from "react";
import { create } from "zustand";
import type { ToolsTreeNode, ToolsType } from "../../model/types";
import { filterToolsTree } from "./filterToolsTree";

export type ToolsRow = { key: string; node: ToolsTreeNode; depth: number; expanded: boolean | null };

type ToolsExpansionStore = {
	opened: Record<ToolsType, Readonly<Record<string, boolean>>>;
	setOpen: (type: ToolsType, key: string, open: boolean) => void;
};

/** One state for the picker and the panel, so both draw a folder the same way, for the whole session. */
export const useToolsExpansionStore = create<ToolsExpansionStore>((set) => ({
	opened: { fragment: {}, template: {} },
	setOpen: (type, key, open) =>
		set((store) => {
			const opened = { ...store.opened[type], [key]: open };
			if (!open) for (const inner of Object.keys(opened)) if (inner.startsWith(`${key}/`)) delete opened[inner];
			return { opened: { ...store.opened, [type]: opened } };
		}),
}));

// An id is a file name: it repeats across folders, and inside one once the tools of a deleted folder move up into
// it. So a row is known by its path from the root, and a name repeated among siblings by its turn.
const withKeys = (nodes: ToolsTreeNode[], parentKey: string): [ToolsTreeNode, string][] => {
	const taken = new Set<string>();
	return nodes.map((node) => {
		let key = `${parentKey}/${node.id}`;
		for (let turn = 1; taken.has(key); turn++) key = `${parentKey}/${node.id}:${turn}`;
		taken.add(key);
		return [node, key];
	});
};

const openRows = (
	nodes: ToolsTreeNode[],
	opened: Readonly<Record<string, boolean>>,
	parentKey = "",
	depth = 0,
): ToolsRow[] =>
	withKeys(nodes, parentKey).flatMap(([node, key]) => {
		if (!node.children.length) return [{ key, node, depth, expanded: null }];
		const expanded = opened[key] ?? false;
		return [{ key, node, depth, expanded }, ...(expanded ? openRows(node.children, opened, key, depth + 1) : [])];
	});

const matchRows = (nodes: ToolsTreeNode[], parentKey = "", depth = 0): ToolsRow[] =>
	withKeys(nodes, parentKey).flatMap(([node, key]) =>
		node.children.length
			? [{ key, node, depth, expanded: true }, ...matchRows(node.children, key, depth + 1)]
			: [{ key, node, depth, expanded: null }],
	);

export const useToolsRows = (type: ToolsType, nodes: ToolsTreeNode[], query: string) => {
	const opened = useToolsExpansionStore((store) => store.opened[type]);
	const setOpen = useToolsExpansionStore((store) => store.setOpen);
	const searching = !!query.trim();
	const rows = useMemo(
		() => (searching ? matchRows(filterToolsTree(nodes, query)) : openRows(nodes, opened)),
		[searching, nodes, query, opened],
	);

	const toggle = (row: ToolsRow) => {
		if (!searching && row.expanded !== null) setOpen(type, row.key, !row.expanded);
	};

	return { rows, toggle, searching };
};
